<template>
  <portal :to="getPortalTitle">
    {{ i18n.t('titles.sms_code_verification') }}
  </portal>
  <portal :to="getPortalSubtitle">
    <div class="mb-6">
      {{ i18n.t('texts.check_identity_sms_code_hint') }}
    </div>
  </portal>
  <div class="d-flex flex-column" style="width: 100%">
    <v-row no-gutters>
      <v-col :cols="12" :md="6">
        <v-text-field
          v-model="form.firstname"
          :class="{
            'mr-2': display.mdAndUp.value,
            'mb-5': !display.mdAndUp.value
          }"
          disabled
          hide-details="auto"
          :label="i18n.t('labels.firstname')"
          variant="outlined"
        />
      </v-col>
      <v-col :cols="12" :md="6">
        <v-text-field
          v-model="form.lastname"
          :class="{ 'ml-2': display.mdAndUp.value }"
          disabled
          hide-details="auto"
          :label="i18n.t('labels.lastname')"
          variant="outlined"
        />
      </v-col>
    </v-row>
    <v-row class="mt-5" no-gutters>
      <v-col cols="12">
        <v-text-field
          v-model="form.displayName"
          class="display_name"
          :error-messages="displayNameErrors"
          hide-details="auto"
          :label="i18n.t('labels.display_name')"
          variant="outlined"
          @update:model-value="clearErrors"
        />
      </v-col>
    </v-row>
    <v-row class="mt-5 mb-2" no-gutters>
      <v-col cols="12">
        <DeepFieldPhone
          v-bind="bindProps"
          id="phone-input"
          v-model="form.phoneNumber"
          :country-label="i18n.t('labels.country')"
          density="compact"
          :error-messages="phoneErrorMessages"
          :hide-details="false"
          :label="i18n.t('labels.phone_number') + '*'"
          :placeholder="i18n.t('placeholders.phone_number')"
          :valid-characters-only="true"
          :validate="validatePhone"
          variant="outlined"
          @update:phone-object="onPhoneUpdate"
        />
      </v-col>
    </v-row>
    <v-row v-if="isRASUnsupportedRegion" no-gutters>
      <v-col class="text-error" cols="12">
        <v-alert full-width type="info" variant="outlined">
          {{ i18n.t('texts.check_identity_ras_unsupported_region_hint') }}
        </v-alert>
      </v-col>
    </v-row>

    <portal :to="getPortalActions">
      <!-- ACTIONS -->
      <VBtnSecondary
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="handleIdentificationCancel"
      />
      <VBtnPrimary
        color="primary"
        :disabled="!isFormValid"
        :loading="deepAdminStore.updateUserPending"
        :text="i18n.t('buttons.save')"
        @click="updateDeepAdminUser"
      />
      <!-- /ACTIONS -->
    </portal>
  </div>
</template>

<script setup lang="ts">
import { DeepFieldPhone } from '@deepcloud/deep-ui-lib';
import AuthService from '@/services/authService.ts';
import { useDeepAdminStore } from '@/stores/deepadmin/deepadmin';
import { useSignStore } from '@/stores/sign.ts';
import { AuthorityService } from '@/types/enums/AuthorityService';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEmitter } from '@/composables/useEmitter';
import type { ParsedPhoneNumber } from 'awesome-phonenumber';
import { useRoute } from 'vue-router';
import { constants } from '@/constants/constants';
import { useDisplay } from 'vuetify/framework';
import { SignatureMode } from '@/types/enums/SignatureMode.ts';
import type { SigneeIssue } from '@/types/SignDocument.ts';
import { useDeepAdminInternalStore } from '@/stores/deepadmin/deepadmin-internal.ts';

type PhoneCountry = {
  iso2: string;
};

const emit = defineEmits(['show-warning', 'done', 'cancel']);
const deepAdminStore = useDeepAdminStore();
const signStore = useSignStore();
const deepAdminInternalStore = useDeepAdminInternalStore();
const i18n = useI18n();
const emitter = useEmitter();
const route = useRoute();
const display = useDisplay();
const user = AuthService.user as {
  given_name?: string;
  family_name?: string;
  display_name?: string;
  sub?: string;
};

const form = reactive({
  firstname: user.given_name ?? '',
  lastname: user.family_name ?? '',
  displayName: user.display_name ?? '',
  phoneNumber: '',
  id: user.sub ?? ''
});

// const phone = ref<ParsedPhoneNumber>();
const errors = ref<string | string[]>();

const bindProps = reactive({
  defaultCountry: 'CH',
  preferredCountries: ['CH', 'IT', 'FR', 'DE', 'AT', 'LI'],
  mode: 'international',
  disabledFetchingCountry: false,
  disabled: false,
  disabledFormatting: false,
  displayFormat: 'international',
  displayFormatDelay: 300,
  displayFormatOnBlur: false,
  required: true,
  enabledFlags: true,
  ignoredCountries: [],
  autocomplete: 'on',
  name: 'number',
  maxLen: constants.PHONE_FIELD.MAX_LENGTH,
  dropdownOptions: { disabledDialCode: true, tabindex: 0 },
  inputOptions: { showDialCode: false, tabindex: 0 },
  enabledCountryCode: false,
  validCharactersOnly: true
});

const displayNameErrors = computed(() => {
  return errors.value || [];
});

// Holds the current parsed phone value
const phone = ref<ParsedPhoneNumber | null>(null);

function onPhoneUpdate(parsed: ParsedPhoneNumber) {
  phone.value = JSON.parse(JSON.stringify(parsed));
}

function canFormatPhone(parsed?: ParsedPhoneNumber | null): boolean {
  return Boolean(parsed && (parsed.valid || parsed.shortValid));
}

function isMobilePhoneValid(parsed?: ParsedPhoneNumber | null): boolean {
  return Boolean(parsed && canFormatPhone(parsed) && parsed.typeIsMobile);
}

// Keep this loose so DeepFieldPhone can format to +41 while the input is focused.
function validatePhone(
  parsed: ParsedPhoneNumber,
  _country: PhoneCountry
): boolean {
  if (!parsed) return true;

  // if (parsed.regionCode !== country.iso2) return false;

  return canFormatPhone(parsed);
}

// Drives the actual error message shown
const invalidPhoneMessage = computed(() => {
  if (!phone.value) return;

  if (!canFormatPhone(phone.value)) return;

  if (phone.value.typeIsMobile) return;

  if (phone.value.regionCode === 'CH') {
    return i18n.t('errors.invalid_swiss_mobile_phone_number');
  }

  return i18n.t('errors.invalid_phone_number');
});

const phoneErrorMessages = computed(() => {
  return invalidPhoneMessage.value ? [invalidPhoneMessage.value] : [];
});

const isFormValid = computed(() => {
  return isMobilePhoneValid(phone.value);
});

const getPortalTitle = computed(() => {
  if (route.name === 'document-batch-sign') {
    return 'batch-identify-dialog-title';
  } else {
    return 'identify-dialog-title';
  }
});

const getPortalSubtitle = computed(() => {
  if (route.name === 'document-batch-sign') {
    return 'batch-identify-dialog-subtitle';
  } else {
    return 'identify-dialog-subtitle';
  }
});

const getPortalActions = computed(() => {
  if (route.name === 'document-batch-sign') {
    return 'batch-identify-dialog-actions';
  } else {
    return 'identify-dialog-actions';
  }
});

const isRASUnsupportedRegion = computed(() => {
  const isSignatureModeAdvanced =
    signStore?.document?.signatureMode === SignatureMode.ADVANCED;
  const ras =
    signStore?.document?.signContext.availableAuthorityServices?.ras ||
    deepAdminInternalStore.signeeIssues?.ras;
  if (
    !ras ||
    (!isSignatureModeAdvanced && signStore?.document) ||
    ras.hasRequiredLevel
  )
    return false;
  return (
    ras.signeeIssues.length > 0 &&
    ras.signeeIssues.some(
      (i: SigneeIssue) => i === 'user.phonenumber.unsupported-region'
    )
  );
});

function clearErrors() {
  errors.value = undefined;
}

function updateDeepAdminUser() {
  if (phone.value && phone.value.regionCode === 'CH') {
    deepAdminStore
      .updateUser(form.id as string, {
        firstName: form.firstname,
        lastName: form.lastname,
        displayName: form.displayName,
        countryOfResidence: phone.value?.regionCode
          ? phone.value?.regionCode
          : '',
        primaryPhoneNumber: form.phoneNumber
      })
      .then(() => {
        signStore.enableAuthorityService(AuthorityService.MOBILE_ID);
        if (deepAdminInternalStore.signeeIssues?.ras) {
          deepAdminInternalStore.signeeIssues.ras = {
            hasRequiredLevel: true,
            signeeIssues: []
          };
        }

        emit('done');
        emitter.$emit('enable-sms-code');
      })
      .catch((e) => {
        errors.value =
          e.response.data?.non_field_errors ||
          e.response.data?.detail ||
          i18n.t('errors.generic');
      });
  } else {
    emit('show-warning');
  }
}

function handleIdentificationCancel() {
  emit('cancel');
}
</script>

<style scoped lang="scss">
.sms-code-title {
  font-weight: 600;
  font-size: 22px;
  line-height: 28px;
  text-align: center;
  color: black;
  margin-bottom: 12px;
}

.sms-code-subtitle {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  text-align: center;
  color: black;
  margin-bottom: 24px;
}

.display_name :deep(.v-input__details) {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 0;
}

:deep(.v-phone-input) {
  .v-field__input {
    padding-top: 0;
    padding-bottom: 0;
  }

  .v-input__details {
    align-items: flex-start;
    height: 30px;
    min-height: 30px;
    overflow: visible;
    padding-top: 6px;
  }
}
</style>
